import {
  isValidCNPJ,
  isValidCPF,
  isValidEmail,
} from "@brazilian-utils/brazilian-utils";
import { Box, Flex, Radio, RadioGroup, Switch } from "@chakra-ui/react";
import { Col, Row } from "antd";
import PrimaryButton from "components/Button/PrimaryButton";
import FormContatos from "components/FormContatos";
import Field from "components/Input/Field";
import InputCnpj from "components/Input/InputCnpj";
import SelectEstadoCivil from "components/Input/SelectEstadoCivil";
import UploadImagem from "components/Input/UploadImagem";
import { PessoaTipo } from "core/features/pessoas/typings";
import { Control, UseFormSetValue } from "react-hook-form";
import { PessoaFisicaOuJuridicaForm } from "resources/types/pessoa";
import OutlineButton from "ui/components/Button/OutlineButton";
import InputMask from "ui/components/Input/InputMask";
import SelectSexo from "ui/components/Input/SelectSexo";
import { CnpjResponse } from "ui/components/Input/InputCnpj";

interface FormPessoaFisicaOuJuridicaProps {
  isEdit?: boolean;
  control: Control<PessoaFisicaOuJuridicaForm, any>;
  setField: UseFormSetValue<PessoaFisicaOuJuridicaForm>;
  form: PessoaFisicaOuJuridicaForm;
  isLoadingSave?: boolean;
  onSubmit?: () => any;
  onCancel?: () => any;
  showActive?: boolean;
  requiredEmail?: boolean;
}

function FormPessoaFisicaOuJuridica({
  isEdit,
  control,
  setField,
  form,
  isLoadingSave,
  onSubmit,
  onCancel,
  showActive,
  requiredEmail,
}: FormPessoaFisicaOuJuridicaProps) {
  const isPessoaFisica = form?.pessoaTipo === PessoaTipo.Fisica;

  const showEmail = isPessoaFisica;

  const afterLoadCnpj = (data: CnpjResponse) => {
    if (data) {
      const { pessoa } = data;

      setField(
        "pessoa.classificacaoTributariaId",
        pessoa.classificacaoTributariaId || undefined
      );
      setField(
        "pessoa.naturezaJuridicaId",
        pessoa.naturezaJuridicaId || undefined
      );
      setField("pessoa.cnaeId", pessoa.cnaeId || undefined);
      setField("pessoa.nome", pessoa.nome);
      setField("pessoa.email", pessoa.email ?? "");
      setField("pessoa.documento", pessoa.documento.replace(/\D/g, ""));
      setField("pessoa.celular", pessoa.celular ?? "");
      setField("pessoa.telefone", pessoa.telefone ?? "");
      setField("pessoa.inscricaoEstadual", pessoa.inscricaoEstadual ?? "");
      setField("pessoa.nomeFantasia", pessoa.nomeFantasia ?? "");
    }
  };

  return (
    <>
      <Box display="flex" as={Row} gutter={16} h="150px" mb={4}>
        <Box as={Col} md={19} display="flex" flexDir="column">
          {!isEdit ? (
            <Field
              control={control}
              name="pessoaTipo"
              rules={{
                required: true,
              }}
              render={({ field, isInvalid }) => (
                <RadioGroup
                  value={field.value}
                  onChange={field.onChange}
                  as={Row}
                  gutter={32}
                  mb={6}
                >
                  <Box as={Col} md={6}>
                    <Radio
                      isInvalid={isInvalid}
                      color={isInvalid ? "red" : undefined}
                      value={PessoaTipo.Fisica}
                    >
                      Pessoa Física
                    </Radio>
                  </Box>
                  <Box as={Col} md={6}>
                    <Radio
                      isInvalid={isInvalid}
                      color={isInvalid ? "red" : undefined}
                      value={PessoaTipo.Juridica}
                    >
                      Pessoa Jurídica
                    </Radio>
                  </Box>
                </RadioGroup>
              )}
            />
          ) : null}
          <Box as={Row} gutter={16} mt="auto">
            <Box as={Col} md={10}>
              <Field
                label={isPessoaFisica ? "CPF" : "CNPJ"}
                control={control}
                name="pessoa.documento"
                render={({ field, isInvalid }) => {
                  return isPessoaFisica ? (
                    <InputMask
                      value={field.value}
                      onChange={(v) => {
                        field.onChange(v.replace(/\D/g, ""));
                      }}
                      isInvalid={isInvalid}
                      mask="999.999.999-99"
                      placeholder="XXX.XXX.XXX-XX"
                    />
                  ) : (
                    <InputCnpj
                      isInvalid={isInvalid}
                      value={field.value}
                      onChange={(v) => {
                        field.onChange(v.replace(/\D/g, ""));
                      }}
                      onLoadCnpj={afterLoadCnpj}
                    />
                  );
                }}
                rules={{
                  required: "Campo obrigatório",
                  validate: (v) => {
                    if (!isPessoaFisica) {
                      return isValidCNPJ(v) || "CNPJ inválido";
                    }
                    if (v.length && !isPessoaFisica) {
                      return isValidCNPJ(v) || "CNPJ inválido";
                    }
                    if (v.length && isPessoaFisica) {
                      return isValidCPF(v) || "CPF inválido";
                    }
                    return true;
                  },
                }}
              />
            </Box>

            <Box as={Col} md={14}>
              <Field
                label={isPessoaFisica ? "Nome" : "Razão Social"}
                control={control}
                name="pessoa.nome"
                inputProps={{
                  placeholder: "",
                }}
                rules={{ required: "Campo obrigatório" }}
              />
            </Box>
          </Box>
        </Box>

        <Flex as={Col} md={5} alignItems="flex-end">
          <Field
            control={control}
            name="pessoa.imagemUrl"
            render={({ field }) => {
              return (
                <UploadImagem
                  {...field}
                  isTenant
                  height="150px"
                  confirmButton={
                    isPessoaFisica ? "Enviar foto" : "Enviar logotipo"
                  }
                />
              );
            }}
          />
        </Flex>
      </Box>

      {isPessoaFisica ? (
        <Box as={Row} gutter={18}>
          <Box as={Col} md={8}>
            <Field
              label="RG"
              control={control}
              name="pessoa.rg"
              inputProps={{
                placeholder: "RG",
              }}
            />
          </Box>

          <Box as={Col} md={6}>
            <Field
              control={control}
              name="pessoa.sexo"
              label="Sexo"
              render={({ field }) => {
                return <SelectSexo {...field} />;
              }}
            />
          </Box>
          <Box as={Col} md={6}>
            <Field
              control={control}
              name="pessoa.estadoCivil"
              label="Estado civil"
              render={({ field }) => {
                return (
                  <SelectEstadoCivil genero={form.pessoa?.sexo} {...field} />
                );
              }}
            />
          </Box>

          <Box as={Col} md={4}>
            <Field
              name="pessoa.dataNascimento"
              label="Data de nascimento"
              inputProps={{ type: "date" }}
              control={control}
            />
          </Box>
        </Box>
      ) : (
        <>
          <Box as={Row} gutter={16}>
            <Box as={Col} md={8}>
              <Field
                label="Nome Fantasia"
                control={control}
                name="pessoa.nomeFantasia"
                inputProps={{
                  placeholder: "Nome Fantasia",
                }}
                rules={{
                  required: "Campo obrigatório",
                }}
              />
            </Box>

            <Box as={Col} md={8}>
              <Field
                label="Inscrição Estadual"
                control={control}
                name="pessoa.inscricaoEstadual"
              />
            </Box>

            <Box as={Col} md={8}>
              <Field
                label="Inscrição Municipal"
                control={control}
                name="pessoa.inscricaoMunicipal"
              />
            </Box>
          </Box>

          <Box as={Row} gutter={16} mt={4}>
            <Box as={Col} md={8}>
              <Field
                label="Email"
                control={control}
                name="pessoa.email"
                inputProps={{
                  type: "email",
                }}
                rules={{
                  required: requiredEmail,
                  validate: (v) => {
                    return !v || isValidEmail(v) || "Email inválido";
                  },
                }}
              />
            </Box>
          </Box>
        </>
      )}
      <Box as={Row} gutter={16}>
        {showEmail ? (
          <Box as={Col} md={8} mt={4}>
            <Field
              label="Email"
              control={control}
              name="pessoa.email"
              inputProps={{
                type: "email",
              }}
              rules={{
                required: requiredEmail,
                validate: (v) => {
                  return !v || isValidEmail(v) || "Email inválido";
                },
              }}
            />
          </Box>
        ) : null}
      </Box>

      <Box mt={8} maxW="60%" minW="500px">
        <FormContatos
          hideCobranca={false}
          control={control}
          name="pessoa.contatos"
        />
      </Box>

      <Flex justifyContent={"flex-end"} mt="32px" gap={4} alignItems="center">
        {showActive ? (
          <Field
            name="ativo"
            control={control}
            shouldUnregister
            render={({ field }) => (
              <Switch
                d="flex"
                alignItems="center"
                size="lg"
                onChange={() => {
                  field.onChange(!field.value);
                }}
                isChecked={!!field.value}
              >
                Cliente Ativo
              </Switch>
            )}
          />
        ) : null}

        {onCancel ? (
          <OutlineButton onClick={onCancel}>Voltar</OutlineButton>
        ) : null}

        <PrimaryButton
          isDisabled={isLoadingSave}
          isLoading={isLoadingSave}
          onClick={onSubmit}
        >
          Salvar
        </PrimaryButton>
      </Flex>
    </>
  );
}

export default FormPessoaFisicaOuJuridica;
